import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";

export default function PodcastFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const audioRef = useRef(null);

  const filterByUser =
    location.state?.filterByUser ||
    new URLSearchParams(location.search).get("userId") ||
    null;
  const filterByUsername =
    location.state?.filterByUsername ||
    new URLSearchParams(location.search).get("username") ||
    null;

  const fetchPosts = useCallback(async (pageNum = 1) => {
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 20,
        ...(filterByUser && { userId: filterByUser }),
      });
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/podcast?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Podcast feed:", error);
      setLoading(false);
    }
  }, [user.token, filterByUser]);

  // Buscar episódios salvos
  const fetchSavedEpisodes = useCallback(async () => {
    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/podcast/saved`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      // Apenas mantém os dados, sem usar
    } catch (error) {
      console.error("Error fetching saved episodes:", error);
    }
  }, [user.token]);

  // Buscar progresso dos episódios
  const fetchEpisodeProgress = useCallback(async () => {
    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/podcast/history?limit=100`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      // Apenas busca, sem usar dados
    } catch (error) {
      console.error("Error fetching episode progress:", error);
    }
  }, [user.token]);

  // Buscar inscrições
  const fetchSubscriptions = useCallback(async () => {
    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/podcast/subscriptions`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      // Apenas busca, sem usar dados
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    }
  }, [user.token]);

  useEffect(() => {
    fetchPosts();
    fetchSavedEpisodes();
    fetchEpisodeProgress();
    fetchSubscriptions();
  }, [fetchPosts, fetchSavedEpisodes, fetchEpisodeProgress, fetchSubscriptions]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const playEpisode = (post) => {
    if (currentEpisode?._id === post._id) {
      togglePlay();
    } else {
      setCurrentEpisode(post);
      setIsPlaying(true);
      setCurrentTime(0);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 30;
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 10;
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const hours = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="podcast_feed_loading">
        <div className="podcast_loader"></div>
        <span>Carregando podcasts...</span>
      </div>
    );
  }

  return (
    <div className="podcast_feed">
      {/* User Filter Banner */}
      {filterByUser && (
        <div className="feed_user_filter_banner">
          <div className="feed_user_filter_content">
            <span className="feed_user_filter_icon">👤</span>
            <span className="feed_user_filter_text">
              Mostrando apenas publicações de <strong>{filterByUsername || "usuário"}</strong> no Podcast
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="podcast_header">
        <h1>Podcasts</h1>
        <p>Ouça episódios incríveis da nossa comunidade</p>
      </div>

      {/* Episodes List */}
      {posts.length === 0 ? (
        <div className="podcast_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z" />
          </svg>
          <h3>Nenhum podcast encontrado</h3>
          <p>Seja o primeiro a publicar um episódio!</p>
        </div>
      ) : (
        <div className="podcast_episodes">
          {posts.map((post) => {
            const podcastData = post.podcastData || {};
            const isCurrentPlaying = currentEpisode?._id === post._id && isPlaying;

            return (
              <div
                key={post._id}
                className={`podcast_episode ${currentEpisode?._id === post._id ? "active" : ""}`}
              >
                <div
                  className="podcast_episode_cover"
                  onClick={() => playEpisode(post)}
                >
                  {podcastData.showCover || post.images?.[0] ? (
                    <img
                      src={podcastData.showCover || post.images[0]?.url || post.images[0]}
                      alt=""
                    />
                  ) : (
                    <div className="podcast_default_cover">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                        <path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z" />
                      </svg>
                    </div>
                  )}
                  <div className="podcast_play_overlay">
                    {isCurrentPlaying ? (
                      <svg viewBox="0 0 24 24" fill="white" width="32" height="32">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="white" width="32" height="32">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </div>
                </div>

                <div className="podcast_episode_info">
                  {podcastData.showName && (
                    <span className="podcast_show_name">{podcastData.showName}</span>
                  )}
                  <h3 className="podcast_episode_title">
                    {podcastData.title || post.text || "Episódio sem título"}
                  </h3>
                  {podcastData.description && (
                    <p className="podcast_episode_desc">{podcastData.description}</p>
                  )}
                  <div className="podcast_episode_meta">
                    <Link to={`/profile/${post.user.username}`} className="podcast_author">
                      <img src={post.user.picture} alt="" />
                      <span>{post.user.first_name}</span>
                    </Link>
                    <span className="podcast_date">
                      <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                    </span>
                    {podcastData.duration && (
                      <span className="podcast_duration">
                        {formatTime(podcastData.duration)}
                      </span>
                    )}
                  </div>
                  {podcastData.guests?.length > 0 && (
                    <div className="podcast_guests">
                      <span>Com: {podcastData.guests.join(", ")}</span>
                    </div>
                  )}
                  <div onClick={(e) => e.stopPropagation()}></div>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div className="podcast_load_more">
              <button onClick={loadMore} disabled={loading}>
                {loading ? "Carregando..." : "Ver mais episódios"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mini Player */}
      {currentEpisode && (
        <div className="podcast_mini_player">
          <audio
            ref={audioRef}
            src={currentEpisode.podcastData?.audioUrl || currentEpisode.videos?.[0]?.url}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            autoPlay
          />

          <div className="podcast_player_info">
            <div className="podcast_player_cover">
              {currentEpisode.podcastData?.showCover || currentEpisode.images?.[0] ? (
                <img
                  src={
                    currentEpisode.podcastData?.showCover ||
                    currentEpisode.images[0]?.url ||
                    currentEpisode.images[0]
                  }
                  alt=""
                />
              ) : (
                <div className="podcast_player_default">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z" />
                  </svg>
                </div>
              )}
            </div>
            <div className="podcast_player_text">
              <span className="podcast_player_title">
                {currentEpisode.podcastData?.title || currentEpisode.text}
              </span>
              <span className="podcast_player_show">
                {currentEpisode.podcastData?.showName || currentEpisode.user.first_name}
              </span>
            </div>
          </div>

          <div className="podcast_player_controls">
            <button className="podcast_skip" onClick={skipBackward} title="-10s">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              </svg>
              <span>10</span>
            </button>

            <button className="podcast_play_btn" onClick={togglePlay}>
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            <button className="podcast_skip" onClick={skipForward} title="+30s">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
              </svg>
              <span>30</span>
            </button>
          </div>

          <div className="podcast_player_progress">
            <span className="podcast_time">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="podcast_progress_bar"
            />
            <span className="podcast_time">{formatTime(duration)}</span>
          </div>

          <div className="podcast_player_extras">
            <select
              value={playbackRate}
              onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
              className="podcast_speed"
            >
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>

            <div className="podcast_volume">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="podcast_volume_slider"
              />
            </div>

            <button
              className="podcast_close_player"
              onClick={() => {
                setCurrentEpisode(null);
                setIsPlaying(false);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
